import { useMemo } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { LabeledPillList } from '@/components/common/LabeledPill';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRememberViewedRow, useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getTableEmptyText } from '@/utils/listSearch';
import type { ViewedRowList } from '@/store/lastViewedRow.store';
import { monitorInfiniteQueryOptions } from '../api/question.api';
import { getInstitutesLink } from '../components/monitorLevelLinks';
import { getMonitorClassColumns, getMonitorDistrictColumns } from '../components/QuestionColumns';
import {
  MONITOR_LEVEL_TITLE,
  MONITOR_SEARCH_PLACEHOLDER,
  type BoardBreakdownGrouping,
} from '../constants/question.constants';
import { useMonitorContextFields } from '../hooks/useMonitorContextFields';
import type { MonitorBoardBreakdownSearch } from '../types/question.types';
import { toBoardBreakdownParams, toCountPillField } from '../utils/monitorLevels';

interface BoardBreakdownLevel {
  title: string;
  countLabel: string;
  errorTitle: string;
  viewedRows: ViewedRowList;
}

const BOARD_BREAKDOWN_LEVEL: Record<BoardBreakdownGrouping, BoardBreakdownLevel> = {
  class: {
    title: MONITOR_LEVEL_TITLE.classes,
    countLabel: 'Classes',
    errorTitle: 'Could not load the classes',
    viewedRows: 'questionMonitorClasses',
  },
  zilla: {
    title: MONITOR_LEVEL_TITLE.districts,
    countLabel: 'Districts',
    errorTitle: 'Could not load the districts',
    viewedRows: 'questionMonitorDistricts',
  },
};

const BOARD_BREAKDOWN_ROUTES = {
  class: getRouteApi('/_auth/question/monitor/classes'),
  zilla: getRouteApi('/_auth/question/monitor/districts'),
} as const satisfies Record<BoardBreakdownGrouping, unknown>;

interface MonitorBoardBreakdownPageProps {
  groupBy: BoardBreakdownGrouping;
  search: MonitorBoardBreakdownSearch;
}

export function MonitorBoardBreakdownPage({ groupBy, search }: MonitorBoardBreakdownPageProps) {
  const navigate = useNavigate();
  const routeNavigate = BOARD_BREAKDOWN_ROUTES[groupBy].useNavigate();
  const level = BOARD_BREAKDOWN_LEVEL[groupBy];
  const params = useMemo(() => toBoardBreakdownParams(search), [search]);
  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void routeNavigate({ search: (previous) => ({ ...previous, search: text }), replace: true });
    },
  });

  useRememberViewedRow('questionMonitorBoards', search.boardId);
  const lastViewedGroupId = useRevealLastViewedRow(level.viewedRows, search.boardId);

  const query = useInfiniteTableQuery(monitorInfiniteQueryOptions(params, groupBy));
  const contextFields = useMonitorContextFields(params);

  const columns = useMemo(() => {
    if (groupBy === 'class') {
      return getMonitorClassColumns((row, isNew) =>
        getInstitutesLink({
          ...params,
          ...(row.groupId === null ? {} : { classId: row.groupId }),
          ...(row.isTpOtherMenu === null ? {} : { isTpOtherMenu: row.isTpOtherMenu }),
          ...(isNew ? { isNew: true } : {}),
          variant: isNew ? 'byAreaNew' : 'byArea',
          from: search.from,
        }),
      );
    }
    return getMonitorDistrictColumns((row) =>
      getInstitutesLink({
        ...params,
        ...(row.groupId === null ? {} : { zillaId: row.groupId }),
        variant: 'byArea',
        from: search.from,
      }),
    );
  }, [groupBy, params, search.from]);

  return (
    <DetailTableLayout
      title={level.title}
      search={{
        value: searchText,
        onChange: setSearchText,
        placeholder: MONITOR_SEARCH_PLACEHOLDER,
      }}
      onNoHistory={() =>
        void navigate({ to: search.from, search: { projectId: search.projectId } })
      }
      summary={
        <LabeledPillList
          fields={[
            ...contextFields,
            toCountPillField({
              key: 'count',
              label: level.countLabel,
              count: query.firstPageMeta?.total,
              isLoading: query.isLoading,
            }),
          ]}
        />
      }
      table={{
        rows: query.rows,
        columns,
        isLoading: query.isLoading,
        error: query.loadError,
        errorTitle: level.errorTitle,
        onRetry: () => void query.refetch(),
        stickyColumnId: 'groupName',
        ...getTableEmptyText(search.search, 'Nothing is assigned on this board'),
        isRowHighlighted: (row) => row.groupId === lastViewedGroupId,
        loadMoreProps: query.loadMoreProps,
      }}
    />
  );
}
